<template>
  <div class="share-secret-page">
    <section class="hero-sub">
      <div class="container">
        <router-link to="/" class="back-link">← {{ $t('secretShare.backToHome') }}</router-link>
        <div class="badge-group">
          <span class="badge">🔒 {{ $t('secretShare.badgeClientCrypto') }}</span>
          <span class="badge badge-accent">🔥 {{ $t('secretShare.badgeBurnOnRead') }}</span>
        </div>
        <h1>{{ $t('secretShare.title') }}</h1>
        <p class="subtitle">{{ $t('secretShare.subtitle') }}</p>
      </div>
    </section>

    <div class="container main-content">
      <!-- DECRYPT / RETRIEVE MODE -->
      <section v-if="isRetrieveMode" class="card retrieve-card">
        <div v-if="!revealed && !fetchError" class="reveal-prompt">
          <div class="warning-box">
            <strong>⚠️ {{ $t('secretShare.retrieve.title') }}</strong>
            <p>{{ $t('secretShare.retrieve.warning') }}</p>
          </div>

          <button
            @click="handleReveal"
            :disabled="revealing"
            class="cta-button burn-button"
          >
            <span v-if="!revealing">🔥 {{ $t('secretShare.retrieve.revealButton') }}</span>
            <span v-else>{{ $t('secretShare.retrieve.revealing') }}</span>
          </button>
        </div>

        <div v-if="revealed" class="revealed-content">
          <label class="form-label">{{ $t('secretShare.retrieve.decryptedLabel') }}</label>
          <div class="secret-box">
            <pre>{{ decryptedSecret }}</pre>
          </div>

          <div class="action-row">
            <button @click="copySecret" class="cta-button action-btn">
              {{ copiedSecret ? $t('secretShare.created.copied') : $t('secretShare.retrieve.copyButton') }}
            </button>
            <button @click="resetToCreate" class="secondary-button action-btn">
              {{ $t('secretShare.retrieve.createOwn') }}
            </button>
          </div>

          <div class="burned-notice">
            {{ $t('secretShare.retrieve.burnedStatus') }}
          </div>
        </div>

        <div v-if="fetchError" class="error-box">
          <p>{{ $t('secretShare.retrieve.errorNotFound') }}</p>
          <button @click="resetToCreate" class="secondary-button" style="margin-top: 16px;">
            {{ $t('secretShare.retrieve.createOwn') }}
          </button>
        </div>
      </section>

      <!-- CREATE SECRET MODE -->
      <section v-else class="card create-card">
        <form v-if="!createdLink" @submit.prevent="handleCreateSecret" class="secret-form">
          <div v-if="createError" class="error-banner">
            ⚠️ {{ $t('secretShare.form.errorCreate') || createError }}
          </div>

          <div class="form-group">
            <label for="secret-text">{{ $t('secretShare.form.secretLabel') }}*</label>
            <textarea
              id="secret-text"
              v-model="secretText"
              rows="6"
              :placeholder="$t('secretShare.form.secretPlaceholder')"
              required
            ></textarea>
          </div>

          <div class="form-group">
            <label for="ttl-select">{{ $t('secretShare.form.ttlLabel') }}</label>
            <select id="ttl-select" v-model="ttl">
              <option value="300">{{ $t('secretShare.form.ttlOptions.300') }}</option>
              <option value="3600">{{ $t('secretShare.form.ttlOptions.3600') }}</option>
              <option value="86400">{{ $t('secretShare.form.ttlOptions.86400') }}</option>
              <option value="259200">{{ $t('secretShare.form.ttlOptions.259200') }}</option>
              <option value="604800">{{ $t('secretShare.form.ttlOptions.604800') }}</option>
            </select>
          </div>

          <button type="submit" :disabled="encrypting" class="cta-button full-width">
            <span v-if="!encrypting">🔒 {{ $t('secretShare.form.submitButton') }}</span>
            <span v-else>{{ $t('secretShare.form.submitting') }}</span>
          </button>
        </form>

        <!-- CREATION SUCCESS RESULT -->
        <div v-else class="created-result">
          <div class="success-header">
            <h3>✓ {{ $t('secretShare.created.title') }}</h3>
            <p>{{ $t('secretShare.created.description') }}</p>
          </div>

          <div class="link-input-group">
            <input
              type="text"
              readonly
              :value="createdLink"
              ref="linkInput"
              class="link-input"
            />
            <button @click="copyLink" class="cta-button copy-btn">
              {{ copiedLink ? $t('secretShare.created.copied') : $t('secretShare.created.copyButton') }}
            </button>
          </div>

          <div class="burn-guarantee">
            ⚠️ <strong>{{ $t('secretShare.created.burnNotice') }}</strong>
          </div>

          <button @click="resetForm" class="secondary-button full-width" style="margin-top: 16px;">
            {{ $t('secretShare.created.createAnother') }}
          </button>
        </div>
      </section>

      <!-- ARCHITECTURE OVERVIEW -->
      <section class="card info-card">
        <h3>{{ $t('secretShare.architecture.title') }}</h3>
        <div class="architecture-grid">
          <div class="arch-item">
            <div class="arch-icon">🔑</div>
            <div class="arch-text">
              <h4>Client-Side AES-GCM-256</h4>
              <p>{{ $t('secretShare.architecture.point1') }}</p>
            </div>
          </div>
          <div class="arch-item">
            <div class="arch-icon">🛡️</div>
            <div class="arch-text">
              <h4>Zero-Knowledge Anchor Key</h4>
              <p>{{ $t('secretShare.architecture.point2') }}</p>
            </div>
          </div>
          <div class="arch-item">
            <div class="arch-icon">🔥</div>
            <div class="arch-text">
              <h4>Atomic Burn-on-Read</h4>
              <p>{{ $t('secretShare.architecture.point3') }}</p>
            </div>
          </div>
          <div class="arch-item">
            <div class="arch-icon">⏳</div>
            <div class="arch-text">
              <h4>Automated Firestore TTL</h4>
              <p>{{ $t('secretShare.architecture.point4') }}</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import {
  generateAesKey,
  exportKeyToBase64,
  importKeyFromBase64,
  encryptSecret,
  decryptSecret,
  parseSecretHash
} from '@/utils/crypto'
import { db } from '@/firebase'
import { collection, addDoc, doc, runTransaction, Timestamp } from 'firebase/firestore'

const route = useRoute()
const router = useRouter()

// Create State
const secretText = ref('')
const ttl = ref('86400')
const encrypting = ref(false)
const createdLink = ref('')
const copiedLink = ref(false)
const createError = ref('')
const linkInput = ref(null)

// Retrieve State
const isRetrieveMode = ref(false)
const secretId = ref('')
const secretKeyB64 = ref('')
const revealing = ref(false)
const revealed = ref(false)
const decryptedSecret = ref('')
const copiedSecret = ref(false)
const fetchError = ref(false)

const getSafeOrigin = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.origin) {
      return window.location.origin
    }
  } catch (e) {
    // jsdom safety
  }
  return ''
}

const getSafeHash = () => {
  try {
    if (typeof window !== 'undefined' && window.location && window.location.hash) {
      return window.location.hash
    }
  } catch (e) {
    // jsdom safety
  }
  return ''
}

const checkHash = () => {
  const hash = getSafeHash() || route.hash || ''
  const parsed = parseSecretHash(hash)
  if (parsed.id && parsed.key) {
    isRetrieveMode.value = true
    secretId.value = parsed.id
    secretKeyB64.value = parsed.key
  } else {
    isRetrieveMode.value = false
    secretId.value = ''
    secretKeyB64.value = ''
  }
}

onMounted(() => {
  checkHash()
  try {
    if (typeof window !== 'undefined') {
      window.addEventListener('hashchange', checkHash)
    }
  } catch (e) {
    // ignore
  }
})

watch(() => route.hash, () => {
  checkHash()
})

const handleCreateSecret = async () => {
  if (!secretText.value.trim()) return
  encrypting.value = true
  createError.value = ''

  try {
    const key = await generateAesKey()
    const keyB64 = await exportKeyToBase64(key)
    const { ciphertext, iv } = await encryptSecret(secretText.value, key)
    const ttlSeconds = parseInt(ttl.value, 10) || 86400

    let id = null

    // 1. Try API endpoint
    try {
      const response = await fetch('/api/secret', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ciphertext,
          iv,
          ttl: ttlSeconds
        })
      })

      if (response.ok) {
        const data = await response.json()
        if (data && data.id) {
          id = data.id
        }
      }
    } catch (apiErr) {
      console.warn('API endpoint unavailable, falling back to direct Firestore:', apiErr)
    }

    // 2. Direct Firestore fallback
    if (!id && db) {
      try {
        const duration = Math.min(Math.max(ttlSeconds, 300), 604800)
        const expiresAt = Timestamp.fromDate(new Date(Date.now() + duration * 1000))
        const docRef = await addDoc(collection(db, 'secrets'), {
          ciphertext,
          iv,
          expiresAt
        })
        id = docRef.id
      } catch (firestoreErr) {
        console.error('Direct Firestore creation failed:', firestoreErr)
      }
    }

    if (!id) {
      throw new Error('Failed to create encrypted secret')
    }

    const origin = getSafeOrigin()
    createdLink.value = `${origin}/share#id=${encodeURIComponent(id)}&key=${encodeURIComponent(keyB64)}`
  } catch (err) {
    console.error('Failed to create encrypted secret:', err)
    createError.value = err.message || 'Failed to create encrypted secret'
  } finally {
    encrypting.value = false
  }
}

const copyLink = async () => {
  if (!createdLink.value) return
  try {
    await navigator.clipboard.writeText(createdLink.value)
    copiedLink.value = true
    setTimeout(() => {
      copiedLink.value = false
    }, 2500)
  } catch (err) {
    console.error('Failed to copy link:', err)
  }
}

const handleReveal = async () => {
  if (!secretId.value || !secretKeyB64.value) return
  revealing.value = true
  fetchError.value = false

  try {
    let payload = null

    // 1. Try API endpoint
    try {
      const response = await fetch(`/api/secret/${encodeURIComponent(secretId.value)}`)
      if (response.ok) {
        payload = await response.json()
      }
    } catch (apiErr) {
      console.warn('API endpoint unavailable, falling back to direct Firestore:', apiErr)
    }

    // 2. Direct Firestore fallback
    if (!payload && db) {
      try {
        const docRef = doc(db, 'secrets', secretId.value)
        payload = await runTransaction(db, async (transaction) => {
          const docSnap = await transaction.get(docRef)
          if (!docSnap.exists()) {
            throw new Error('NOT_FOUND')
          }
          const data = docSnap.data()
          transaction.delete(docRef)
          return data
        })

        if (payload.expiresAt) {
          const expDate = payload.expiresAt.toDate ? payload.expiresAt.toDate() : new Date(payload.expiresAt)
          if (expDate < new Date()) {
            throw new Error('EXPIRED')
          }
        }
      } catch (firestoreErr) {
        console.error('Direct Firestore retrieval failed:', firestoreErr)
        if (firestoreErr.message === 'NOT_FOUND' || firestoreErr.message === 'EXPIRED') {
          throw firestoreErr
        }
      }
    }

    if (!payload || !payload.ciphertext || !payload.iv) {
      throw new Error('NOT_FOUND')
    }

    const cryptoKey = await importKeyFromBase64(secretKeyB64.value)
    const plaintext = await decryptSecret(payload.ciphertext, payload.iv, cryptoKey)

    decryptedSecret.value = plaintext
    revealed.value = true
  } catch (err) {
    console.error('Failed to retrieve or decrypt secret:', err)
    fetchError.value = true
  } finally {
    revealing.value = false
  }
}

const copySecret = async () => {
  if (!decryptedSecret.value) return
  try {
    await navigator.clipboard.writeText(decryptedSecret.value)
    copiedSecret.value = true
    setTimeout(() => {
      copiedSecret.value = false
    }, 2500)
  } catch (err) {
    console.error('Failed to copy secret:', err)
  }
}

const resetForm = () => {
  secretText.value = ''
  createdLink.value = ''
  copiedLink.value = false
  createError.value = ''
}

const resetToCreate = () => {
  isRetrieveMode.value = false
  secretId.value = ''
  secretKeyB64.value = ''
  revealed.value = false
  decryptedSecret.value = ''
  fetchError.value = false
  createError.value = ''
  window.location.hash = ''
  router.push('/share')
}
</script>

<style scoped>
.share-secret-page {
  padding-bottom: 80px;
}

.hero-sub {
  background-color: var(--primary-light, #e2f1ff);
  padding: 60px 0 40px 0;
  text-align: center;
}

.back-link {
  display: inline-block;
  color: var(--primary-color, #0055ff);
  font-weight: 600;
  margin-bottom: 20px;
  text-decoration: none;
  font-size: 0.95rem;
}

.back-link:hover {
  text-decoration: underline;
}

.badge-group {
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.badge {
  background-color: #ffffff;
  color: var(--primary-color, #0055ff);
  border: 1px solid rgba(0, 85, 255, 0.2);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.badge-accent {
  background-color: #fef2f2;
  color: #dc2626;
  border-color: #fecaca;
}

.hero-sub h1 {
  font-size: 2.4rem;
  font-weight: 800;
  color: #0f172a;
  margin-bottom: 12px;
}

.hero-sub .subtitle {
  font-size: 1.1rem;
  color: #475569;
  max-width: 680px;
  margin: 0 auto;
  line-height: 1.6;
}

.main-content {
  max-width: 760px;
  margin: 40px auto 0 auto;
}

.card {
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
  padding: 32px;
  margin-bottom: 30px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-weight: 600;
  font-size: 0.92rem;
  margin-bottom: 8px;
  color: #1e293b;
}

textarea, select, .link-input {
  width: 100%;
  padding: 12px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.95rem;
  background-color: #f8fafc;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;
}

textarea:focus, select:focus {
  outline: none;
  background-color: #ffffff;
  border-color: var(--primary-color, #0055ff);
  box-shadow: 0 0 0 3px rgba(0, 85, 255, 0.15);
}

.cta-button {
  background-color: var(--primary-color, #0055ff);
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 14px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
}

.cta-button:hover {
  background-color: var(--primary-dark, #0044cc);
}

.cta-button:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
}

.burn-button {
  background-color: #dc2626;
  width: 100%;
}

.burn-button:hover {
  background-color: #b91c1c;
}

.secondary-button {
  background-color: #f1f5f9;
  color: #334155;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 12px 20px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.secondary-button:hover {
  background-color: #e2e8f0;
}

.full-width {
  width: 100%;
}

.link-input-group {
  display: flex;
  gap: 10px;
  margin: 16px 0;
}

.link-input {
  font-family: monospace;
  font-size: 0.9rem;
  background: #ffffff;
}

.copy-btn {
  white-space: nowrap;
  padding: 12px 20px;
}

.warning-box {
  background-color: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 18px;
  margin-bottom: 24px;
  color: #92400e;
}

.warning-box strong {
  display: block;
  margin-bottom: 6px;
  font-size: 1rem;
}

.warning-box p {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.5;
}

.burn-guarantee {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.88rem;
  margin-top: 14px;
}

.secret-box {
  background-color: #0f172a;
  color: #f8fafc;
  padding: 18px;
  border-radius: 8px;
  margin: 12px 0 20px 0;
  max-height: 350px;
  overflow-y: auto;
}

.secret-box pre {
  margin: 0;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 0.95rem;
  white-space: pre-wrap;
  word-break: break-all;
}

.action-row {
  display: flex;
  gap: 12px;
}

.action-btn {
  flex: 1;
}

.burned-notice {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  font-weight: 600;
  text-align: center;
  padding: 14px;
  border-radius: 8px;
  margin-top: 20px;
  font-size: 0.92rem;
}

.error-box {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  padding: 24px;
  border-radius: 8px;
  text-align: center;
}

.error-banner {
  background-color: #fef2f2;
  border: 1px solid #fecaca;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 18px;
  font-size: 0.92rem;
  font-weight: 500;
}

.success-header h3 {
  color: #16a34a;
  font-size: 1.2rem;
  margin-bottom: 6px;
}

.success-header p {
  color: #475569;
  font-size: 0.92rem;
}

.info-card h3 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 20px;
}

.architecture-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

@media (max-width: 640px) {
  .architecture-grid {
    grid-template-columns: 1fr;
  }
  .link-input-group {
    flex-direction: column;
  }
  .action-row {
    flex-direction: column;
  }
}

.arch-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.arch-icon {
  font-size: 1.5rem;
  background: var(--primary-light, #e2f1ff);
  width: 42px;
  height: 42px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.arch-text h4 {
  font-size: 0.98rem;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.arch-text p {
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.5;
  margin: 0;
}
</style>
